import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useUpdateMerchandisingCollectionSlice } from '@/hooks/useUpdateMerchandisingCollectionSlice';
import i18n from '@/lib/i18n';
import { MerchandisingCollectionSlice } from '@/services/MerchandisingCollectionsService';

const formSchema = z
  .object({
    name: z.string().min(1, {
      message: i18n.t(
        'pages.MerchandisingCollections.pages.Slice.components.SliceInfo.form.nameError',
      ),
    }),
    startsAt: z.string().min(1, {
      message: i18n.t(
        'pages.MerchandisingCollections.pages.Slice.components.SliceInfo.form.startsAtError1',
      ),
    }),
    endsAt: z.string().min(1, {
      message: i18n.t(
        'pages.MerchandisingCollections.pages.Slice.components.SliceInfo.form.endsAtError1',
      ),
    }),
  })
  .superRefine((data, ctx) => {
    const start = new Date(data.startsAt);
    const end = new Date(data.endsAt);

    if (start > end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t(
          'pages.MerchandisingCollections.pages.Slice.components.SliceInfo.form.startsAtError2',
        ),
        path: ['startsAt'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t(
          'pages.MerchandisingCollections.pages.Slice.components.SliceInfo.form.endsAtError2',
        ),
        path: ['endsAt'],
      });
    }
  });

type FormData = z.infer<typeof formSchema>;

interface SliceFormProps {
  slice: MerchandisingCollectionSlice;
}

export function SliceForm({ slice }: SliceFormProps) {
  const [editable, setEditable] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Slice.components.SliceInfo',
  });

  const { updateMerchandisingCollectionSlice, isLoading } =
    useUpdateMerchandisingCollectionSlice();

  const {
    formState: { isSubmitting, errors },
    register,
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    disabled: !editable || isLoading,
    defaultValues: {
      name: slice.name,
      startsAt: format(new Date(slice.startsAt), "yyyy-MM-dd'T'HH:mm"),
      endsAt: format(new Date(slice.endsAt), "yyyy-MM-dd'T'HH:mm"),
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await updateMerchandisingCollectionSlice({
        collectionId: slice.collectionId,
        sliceId: slice.id,
        dto: {
          name: data.name,
          startsAt: new Date(data.startsAt),
          endsAt: new Date(data.endsAt),
        },
      });

      toast.success(t('toast.success'));
      setEditable(false);
    } catch {
      toast.error(t('toast.error'));
    }
  });

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormGroup error={{ message: errors.name?.message, font: 'sm' }}>
          <Label htmlFor="slice-name">{t('form.nameLabel')}</Label>
          <Input id="slice-name" {...register('name')} />
        </FormGroup>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormGroup error={{ message: errors.startsAt?.message, font: 'sm' }}>
          <Label htmlFor="starts-at">{t('form.startsAtLabel')}</Label>

          <Input
            id="starts-at"
            type="datetime-local"
            {...register('startsAt')}
          />
        </FormGroup>

        <FormGroup error={{ message: errors.endsAt?.message, font: 'sm' }}>
          <Label htmlFor="ends-at">{t('form.endsAtLabel')}</Label>

          <Input id="ends-at" type="datetime-local" {...register('endsAt')} />
        </FormGroup>
      </div>

      <div className="flex items-center gap-2">
        {!editable && (
          <Button type="button" onClick={() => setEditable(true)}>
            {t('form.updateButtonLabel')}
          </Button>
        )}

        {editable && (
          <>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t('form.submitButtonLabelLoading')
                : t('form.submitButtonLabel')}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={() => setEditable(false)}
            >
              {t('form.cancelButtonLabel')}
            </Button>
          </>
        )}
      </div>
    </form>
  );
}
